import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import {
    FilterBar,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getAffiliateClicks } from '@/modules/Affiliate/services'
import { AffiliateClick } from '@/modules/Affiliate/types'
import { formatFromUTC } from '@/utils/function'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface ClicksProps {
    affiliateId: number
}

interface ClickFilters {
    converted: string
}

const Clicks: React.FC<ClicksProps> = ({ affiliateId }) => {
    const { t } = useTranslation('affiliate')

    const {
        params,
        filters,
        queryParams,
        setParam,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ClickFilters>({
        defaults: { page: 1, page_size: 10 },
        filterDefaults: { converted: '' },
    })

    const { control } = useForm<ClickFilters>({
        defaultValues: filters,
        values: filters,
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.AFFILIATE_CLICKS,
            affiliateId,
            queryParams,
            filters,
        ] as const,
        queryFn: () =>
            getAffiliateClicks(affiliateId, {
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
                // converted: filters.converted,
            }),
        enabled: !!affiliateId,
        placeholderData: true,
    })

    const clicks = data?.results || []
    const pagination = data?.pagination
    const stateComponent = renderState()

    const filterItems: FilterItemConfig[] = useMemo(
        () => [
            {
                name: 'converted',
                type: 'select',
                label: t('converted'),
                placeholder: t('converted'),
                options: [
                    { label: t('yes'), value: 'true' },
                    { label: t('no'), value: 'false' },
                ],
            },
        ],
        [t]
    )

    const columns: DataTableColumn<AffiliateClick>[] = useMemo(
        () => [
            {
                key: 'click_id',
                title: t('click_id'),
                width: 100,
            },
            {
                key: 'visitor_id',
                title: t('visitor_id'),
                width: 200,
                render: (value) => (
                    <span className="font-mono text-xs">{value || '-'}</span>
                ),
            },
            {
                key: 'ip_address',
                title: t('ip_address'),
                width: 130,
                render: (value) => value || '-',
            },
            {
                key: 'referer_url',
                title: t('referer'),
                width: 200,
                render: (value) => (
                    <span className="truncate block max-w-[200px]">
                        {value || '-'}
                    </span>
                ),
            },
            {
                key: 'utm_source',
                title: t('utm_source'),
                width: 120,
                render: (value) => value || '-',
            },
            {
                key: 'promo_code',
                title: t('promo_code'),
                width: 120,
                render: (value) => (
                    <span className="font-mono">{value || '-'}</span>
                ),
            },
            {
                key: 'converted',
                title: t('converted'),
                width: 100,
                formatter: {
                    // `converted` is a boolean — use the options formatter (matches opt.value
                    // strictly against the cell value, so the option values must be booleans,
                    // not the strings 'true'/'false'). type:'chip' expects a colorMap, not
                    // options, so it was reading colorMap[false] on undefined and crashing.
                    type: 'options',
                    options: [
                        { label: t('yes'), value: true, color: 'success' },
                        { label: t('no'), value: false, color: 'default' },
                    ],
                },
            },
            {
                key: 'clicked_at',
                title: t('clicked_at'),
                width: 160,
                render: (value) => formatFromUTC(value, 'DD/MM/YYYY HH:mm'),
            },
        ],
        [t]
    )

    const handlePageChange = (page: number) => setParam('page', page, false)
    const handlePageSizeChange = (size: number) => {
        setParam('page_size', size)
        setParam('page', 1)
    }

    return (
        <Paper className="space-y-2 my-2">
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
            />
            {stateComponent || (
                <DataTableCustomized<AffiliateClick>
                    columns={columns}
                    rows={clicks}
                    rowKey={(row) => row.click_id}
                    size="small"
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search_affiliate'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'affiliate-clicks',
                        },
                    }}
                    stickyHeader={{
                        enabled: true,
                        offsetTopSelector: '#main-header',
                    }}
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                    }}
                />
            )}
        </Paper>
    )
}

export default Clicks
